import { ROI } from '@amzn/awscat-portfolio-assessment-types';
import { Layout } from 'plotly.js';
import PlotlyBasic, { Data } from 'plotly.js-basic-dist';
import { createRef, useEffect, useState } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';

import { AWS_ORANGE_COLOR, ON_PREMISES_COLOR } from '../../common/colors';
import { constants } from '../../common/Localization';
import { getCurrencySymbol } from '../../common/LocalizationHelper';
import { useResize } from '../../common/resize-hook';
import { deepCopy } from '../../common/util';
import { DistributionTime } from './interfaces';

const PlotlyChart = createPlotlyComponent(PlotlyBasic);

interface Props {
    roi: ROI;
    currency: string;
    trancheUnit: DistributionTime;
}

function MigrationCashflowGraph({ currency, roi, trancheUnit }: Props) {
    const componentRef = createRef<HTMLDivElement>();
    const graphDimensions = useResize(componentRef);

    const [ graphData, setGraphData ] = useState<Partial<Data>[]>([]);

    useEffect(() => {
        const trancheList: number[] = new Array(roi.totalCostWithMigrationCashFlow.perTranche.length + 1).fill(0).map((value, index) => {
            return index;
        });
        const onPremisesCashFlow: number[] = roi.onPremisesCashFlow.withoutMigration.perTranche;
        const onPremisesWithoutMigrationTrancheCost = deepCopy(onPremisesCashFlow[0]);
        onPremisesCashFlow.unshift(onPremisesWithoutMigrationTrancheCost);
        const awsCashflow: number[] = deepCopy(roi.totalCostWithMigrationCashFlow.perTranche);
        awsCashflow.unshift(onPremisesWithoutMigrationTrancheCost);

        const awsData: Partial<Data> = {
            type: 'scatter',
            x: trancheList,
            y: awsCashflow,
            hoverinfo: 'y',
            mode: 'lines+markers',
            name: constants.COST_WITH_MIGRATION,
            line: {
                color: AWS_ORANGE_COLOR,
            }
        };

        const onPremisesData: Partial<Data> = {
            type: 'scatter',
            x: trancheList,
            y: onPremisesCashFlow,
            hoverinfo: 'y',
            mode: 'lines+markers',
            name: constants.COST_WITHOUT_MIGRATION,
            line: {
                color: ON_PREMISES_COLOR,
            }
        };

        setGraphData([awsData, onPremisesData]);
    }, [roi]);

    const layout: Partial<Layout> = {
        showlegend: true,
        legend: {
            x: 1, y: 100,
            xanchor: 'right'
        },
        xaxis: {
            title: `${constants.TIMELINE_IN} ${trancheUnit.label}s`,
            hoverformat: '.2s',
            dtick: 1,
            rangemode: 'tozero'
        },
        yaxis: {
            title: `${constants.COST_IN} ${getCurrencySymbol(currency)} (${currency})`,
            hoverformat: '.2s',
            rangemode: 'tozero'
        },
        width: graphDimensions.width
    };

    return (
        <div ref={componentRef}>
            <PlotlyChart data={graphData} layout={layout}/>
        </div>
    );
}

export default MigrationCashflowGraph;
